import {
  GridChunkAssembler,
  GridProtocolError,
  GRID_COLS_MAX,
  GRID_COLS_MIN,
  GRID_ROWS_MAX,
  GRID_ROWS_MIN,
  type GridProtocolErrorCode,
  type DecodedGridRecord,
  type DecodedFontRecord,
  type DecodedGridRun,
  type DecodedScreenFrame,
  type GridColor,
} from './protocol'

export type GridRecoveryReason = 'decode' | 'gap' | 'generation' | 'context'

export type GridIngestResult =
  | { kind: 'incomplete' }
  | { kind: 'full-applied' }
  | { kind: 'patch-applied' }
  | { kind: 'font-delivered' }
  | { kind: 'rejected'; reason: GridRecoveryReason }

export type GridCellColor =
  | Readonly<{ type: 'default' }>
  | Readonly<{ type: 'basic'; value: number }>
  | Readonly<{ type: 'ansi256'; value: number }>
  | Readonly<{ type: 'rgb'; value: readonly [number, number, number] }>

export interface GridCell {
  readonly text: string
  readonly style: number
  readonly foreground: GridCellColor
  readonly background: GridCellColor
}

export interface GridModelOptions {
  onRecoveryRequired?: (reason: GridRecoveryReason) => void
  /** A delivery failure enters context recovery but leaves the displayed grid intact. */
  onFontRecord?: (record: DecodedFontRecord) => void
}

export interface GridCursor {
  readonly visible: boolean
  readonly col: number
  readonly row: number
}

interface GridFrameHeader {
  kind: 'full' | 'patch'
  sequence: number
  previousSequence: number
  configGeneration: number
  resizeGeneration: number
  cols: number
  rows: number
  cursor: GridCursor
  runs: DecodedGridRun[]
}

interface GridState {
  cols: number
  rows: number
  cursor: GridCursor
  sequence: number
  configGeneration: number
  resizeGeneration: number
  cells: readonly GridCell[]
}

const EMPTY_GRID_STATE: GridState = Object.freeze({
  cols: 0,
  rows: 0,
  cursor: Object.freeze({ visible: false, col: 0, row: 0 }),
  sequence: 0,
  configGeneration: 0,
  resizeGeneration: 0,
  cells: Object.freeze([]),
})

export class GridModelError extends Error {
  constructor(readonly reason: GridRecoveryReason, message: string) {
    super(message)
    this.name = 'GridModelError'
  }
}

function modelFail(message: string, reason: GridRecoveryReason = 'context'): never {
  throw new GridModelError(reason, `invalid full frame: ${message}`)
}

function patchFail(message: string, reason: GridRecoveryReason = 'decode'): never {
  throw new GridModelError(reason, `invalid patch frame: ${message}`)
}

function isUint(value: unknown, maximum: number): value is number {
  return typeof value === 'number' && Number.isInteger(value) && value >= 0 && value <= maximum
}


type ModelFailure = (message: string) => never

function cloneColor(color: GridColor, fail: ModelFailure = modelFail): GridCellColor {
  if (color === null || typeof color !== 'object') fail('invalid color')
  switch (color.type) {
    case 'default':
      return Object.freeze({ type: 'default' } as const)
    case 'basic':
      if (!isUint(color.value, 15)) fail('invalid basic color')
      return Object.freeze({ type: 'basic', value: color.value } as const)
    case 'ansi256':
      if (!isUint(color.value, 0xff)) fail('invalid indexed color')
      return Object.freeze({ type: 'ansi256', value: color.value } as const)
    case 'rgb': {
      const value = color.value
      if (!Array.isArray(value) || value.length !== 3) {
        fail('invalid RGB color')
      }
      const red = value[0]
      const green = value[1]
      const blue = value[2]
      if (!isUint(red, 0xff) || !isUint(green, 0xff) || !isUint(blue, 0xff)) fail('invalid RGB color')
      return Object.freeze({ type: 'rgb', value: Object.freeze([red, green, blue]) as readonly [number, number, number] } as const)
    }
    default:
      return fail('invalid color type')
  }
}

// The single-scalar checks below already bound the length; the UTF-8 byte
// count they used to compute was only ever compared against cell.text === ''
// downstream, and encoding every cell of a full frame to learn nothing costs
// up to 100k allocations on the path to first paint.
function validateScalar(text: unknown, fail: ModelFailure = modelFail): { text: string } {
  if (typeof text !== 'string') fail('invalid cell text')
  if (text.length === 1) {
    const codeUnit = text.charCodeAt(0)
    if (codeUnit >= 0xd800 && codeUnit <= 0xdfff) fail('cell text is not one scalar')
  } else if (text.length === 2) {
    const high = text.charCodeAt(0)
    const low = text.charCodeAt(1)
    if (high < 0xd800 || high > 0xdbff || low < 0xdc00 || low > 0xdfff) fail('cell text is not one scalar')
  } else if (text.length !== 0) {
    fail('cell text is not one scalar')
  }
  return { text }
}

function validateFrameHeader(frame: GridFrameHeader): number {
  if (frame.kind !== 'full') modelFail('not a full frame')
  if (!isUint(frame.sequence, 0xffffffff) || frame.previousSequence !== 0 ||
      !isUint(frame.configGeneration, 0xffffffff) || !isUint(frame.resizeGeneration, 0xffffffff)) {
    modelFail('invalid frame generations', 'generation')
  }
  if (!isUint(frame.cols, GRID_COLS_MAX) || frame.cols < GRID_COLS_MIN ||
      !isUint(frame.rows, GRID_ROWS_MAX) || frame.rows < GRID_ROWS_MIN) {
    modelFail('screen dimensions out of bounds')
  }
  if (frame.cursor === null || typeof frame.cursor !== 'object' || typeof frame.cursor.visible !== 'boolean' ||
      !isUint(frame.cursor.col, 0xffff) || !isUint(frame.cursor.row, 0xffff) ||
      frame.cursor.col >= frame.cols || frame.cursor.row >= frame.rows) {
    modelFail('cursor out of bounds')
  }
  if (!Array.isArray(frame.runs)) modelFail('invalid runs')
  return frame.cols * frame.rows
}

function copyFrameHeader(frame: DecodedScreenFrame): GridFrameHeader {
  const cursor = frame.cursor
  return {
    kind: frame.kind,
    sequence: frame.sequence,
    previousSequence: frame.previousSequence,
    configGeneration: frame.configGeneration,
    resizeGeneration: frame.resizeGeneration,
    cols: frame.cols,
    rows: frame.rows,
    cursor: { visible: cursor.visible, col: cursor.col, row: cursor.row },
    runs: frame.runs,
  }
}

function buildCells(frame: GridFrameHeader, cellCount: number): readonly GridCell[] {
  const cells = new Array<GridCell>(cellCount)
  let expectedStart = 0
  for (const run of frame.runs) {
    if (run === null || typeof run !== 'object') modelFail('invalid run')
    const start = run.start
    const count = run.count
    const style = run.style
    const chars = run.chars
    if (!isUint(start, 0xffffffff) ||
        !isUint(count, 0xffff) || count === 0 || start !== expectedStart ||
        count > cellCount - start || !isUint(style, 0xff) || !Array.isArray(chars) || chars.length !== count) {
      modelFail('runs do not exactly cover the screen')
    }
    const foreground = cloneColor(run.foreground)
    const background = cloneColor(run.background)
    for (let offset = 0; offset < count; offset++) {
      const scalar = validateScalar(chars[offset])
      cells[start + offset] = Object.freeze({
        ...scalar,
        style,
        foreground,
        background,
      })
    }
    expectedStart += count
  }
  if (expectedStart !== cellCount) modelFail('runs do not exactly cover the screen')
  return Object.freeze(cells)
}

function buildState(frame: DecodedScreenFrame): GridState {
  // Validate the untrusted source before and after copying it. The copied header
  // is the only frame metadata read after this point.
  validateFrameHeader(frame)
  const header = copyFrameHeader(frame)
  const cellCount = validateFrameHeader(header)
  return Object.freeze({
    cols: header.cols,
    rows: header.rows,
    cursor: Object.freeze({ ...header.cursor }),
    sequence: header.sequence,
    configGeneration: header.configGeneration,
    resizeGeneration: header.resizeGeneration,
    cells: buildCells(header, cellCount),
  })
}

function validatePatchHeader(frame: GridFrameHeader, state: GridState): number {
  if (frame.kind !== 'patch') patchFail('not a patch frame')
  if (!isUint(frame.sequence, 0xffffffff) || !isUint(frame.previousSequence, 0xffffffff) ||
      frame.previousSequence === 0 || frame.previousSequence !== state.sequence ||
      state.sequence === 0xffffffff || frame.sequence !== state.sequence + 1) {
    patchFail('sequence gap', 'gap')
  }
  if (!isUint(frame.configGeneration, 0xffffffff) ||
      !isUint(frame.resizeGeneration, 0xffffffff) ||
      frame.configGeneration !== state.configGeneration ||
      frame.resizeGeneration !== state.resizeGeneration ||
      !isUint(frame.cols, GRID_COLS_MAX) || frame.cols < GRID_COLS_MIN ||
      !isUint(frame.rows, GRID_ROWS_MAX) || frame.rows < GRID_ROWS_MIN ||
      frame.cols !== state.cols || frame.rows !== state.rows) {
    patchFail('generation or dimensions do not match', 'generation')
  }
  if (frame.cursor === null || typeof frame.cursor !== 'object' ||
      typeof frame.cursor.visible !== 'boolean' ||
      !isUint(frame.cursor.col, 0xffff) || !isUint(frame.cursor.row, 0xffff) ||
      frame.cursor.col >= frame.cols || frame.cursor.row >= frame.rows) {
    patchFail('cursor out of bounds')
  }
  if (!Array.isArray(frame.runs)) patchFail('invalid runs')
  return frame.cols * frame.rows
}

function buildPatchedState(frame: DecodedScreenFrame, state: GridState): GridState {
  validatePatchHeader(frame, state)
  const header = copyFrameHeader(frame)
  const cellCount = validatePatchHeader(header, state)
  const cells = header.runs.length === 0 ? undefined : state.cells.slice()
  let previousEnd = 0
  for (const run of header.runs) {
    if (run === null || typeof run !== 'object') patchFail('invalid run')
    const start = run.start
    const count = run.count
    const style = run.style
    const chars = run.chars
    if (!isUint(start, 0xffffffff) || !isUint(count, 0xffff) || count === 0 ||
        start < previousEnd || start > cellCount || count > cellCount - start ||
        !isUint(style, 0xff) || !Array.isArray(chars) || chars.length !== count) {
      patchFail('runs overlap, are unordered, or out of bounds')
    }
    const foreground = cloneColor(run.foreground, patchFail)
    const background = cloneColor(run.background, patchFail)
    for (let offset = 0; offset < count; offset++) {
      const scalar = validateScalar(chars[offset], patchFail)
      cells![start + offset] = Object.freeze({
        ...scalar,
        style,
        foreground,
        background,
      })
    }
    previousEnd = start + count
  }
  return Object.freeze({
    cols: header.cols,
    rows: header.rows,
    cursor: Object.freeze({ ...header.cursor }),
    sequence: header.sequence,
    configGeneration: header.configGeneration,
    resizeGeneration: header.resizeGeneration,
    cells: cells === undefined ? state.cells : Object.freeze(cells),
  })
}

export class GridModel {
  readonly assembler = new GridChunkAssembler()
  readonly onRecoveryRequired?: (reason: GridRecoveryReason) => void
  readonly onFontRecord?: (record: DecodedFontRecord) => void

  private state: GridState = EMPTY_GRID_STATE

  private recoverySuppressed = false

  constructor(options: GridModelOptions = {}) {
    this.onRecoveryRequired = options.onRecoveryRequired
    this.onFontRecord = options.onFontRecord
  }

  get cols(): number { return this.state.cols }
  get rows(): number { return this.state.rows }
  get cursor(): GridCursor { return this.state.cursor }
  get sequence(): number { return this.state.sequence }
  get configGeneration(): number { return this.state.configGeneration }
  get resizeGeneration(): number { return this.state.resizeGeneration }
  get cells(): readonly GridCell[] { return this.state.cells }

  clone(): GridModel {
    const clone = new GridModel()
    clone.state = Object.freeze({
      cols: this.state.cols,
      rows: this.state.rows,
      cursor: Object.freeze({ ...this.state.cursor }),
      sequence: this.state.sequence,
      configGeneration: this.state.configGeneration,
      resizeGeneration: this.state.resizeGeneration,
      // Grid states, their cell arrays, and every nested cell/color value are
      // frozen when published. A staging clone can therefore share the exact
      // snapshot until the first changed patch allocates a new flat array.
      cells: this.state.cells,
    })
    return clone
  }

  ingest(message: ArrayBuffer | Uint8Array): GridIngestResult {
    try {
      const record = this.assembler.push(message)
      if (record === null) return { kind: 'incomplete' }
      return this.applyRecord(record)
    } catch (error) {
      const reason = this.recoveryReason(error)
      this.enterRecovery(reason)
      return { kind: 'rejected', reason }
    }
  }

  applyFull(frame: DecodedScreenFrame): void {
    try {
      const state = buildState(frame)
      this.state = state
      this.recoverySuppressed = false
    } catch (error) {
      this.enterRecovery(error instanceof GridModelError ? this.recoveryReason(error) : 'context')
      throw error
    }
  }

  applyPatch(frame: DecodedScreenFrame): void {
    try {
      if (this.recoverySuppressed) patchFail('full frame required during recovery', 'gap')
      this.state = buildPatchedState(frame, this.state)
    } catch (error) {
      this.enterRecovery(error instanceof GridModelError ? this.recoveryReason(error) : 'context')
      throw error
    }
  }

  private applyRecord(record: DecodedGridRecord): GridIngestResult {
    if (record.kind === 'font') {
      if (this.onFontRecord === undefined) {
        this.enterRecovery('context')
        return { kind: 'rejected', reason: 'context' }
      }
      try {
        this.onFontRecord(record.font)
        return { kind: 'font-delivered' }
      } catch {
        this.enterRecovery('context')
        return { kind: 'rejected', reason: 'context' }
      }
    }
    if (record.frame.kind === 'patch') {
      this.applyPatch(record.frame)
      return { kind: 'patch-applied' }
    }
    this.applyFull(record.frame)
    return { kind: 'full-applied' }
  }

  private recoveryReason(error: unknown): GridRecoveryReason {
    if (error instanceof GridModelError) return error.reason
    if (error instanceof GridProtocolError) return this.protocolReason(error.code)
    return 'decode'
  }

  private protocolReason(code: GridProtocolErrorCode): Extract<GridRecoveryReason, 'decode' | 'gap'> {
    return code
  }

  private enterRecovery(reason: GridRecoveryReason): void {
    this.assembler.resetForRecovery()
    if (this.recoverySuppressed) return
    this.recoverySuppressed = true
    try {
      this.onRecoveryRequired?.(reason)
    } catch {
      // Recovery remains active even if an application callback is unavailable.
    }
  }
}
